<template>
	<Drawer initialWidth="xl" @requestUnmount="$emit('requestClose')">

		<template #header>
			<span class="font-semibold text-md">{{ $t('Evaluate') }}</span>
		</template>

		<template #default="{ hide }">
			<form @submit.prevent="save" class="p-6 flex flex-col gap-8">

				<div class="flex flex-col gap-8">
					<div v-for="goal in distinctGoals" :key="goal.id" class="flex flex-col">
						<div class="flex items-start gap-4">
							<MapPinIcon class="w-6 h-6 shrink-0" />
							<GoalTitle :goal="goal" :showPath="true" />
						</div>
						<div class="pl-10">
							<PupilSelectionSummary
								:selectedPupils="pupilsForGoal(goal)"
								:allPupils="allPupils"
								:showSymbols="true"
								class="mt-2"
							/>
						</div>
					</div>
				</div>

				<EvaluationLevelPicker v-model="level" />

				<div class="flex gap-3 h-0 transition-all overflow-hidden" :class="[level ? 'h-24' : '']">
					<textarea
						data-test="note"
						ref="note"
						class="textarea textarea-bordered h-24 w-full"
						:disabled="!level"
						:placeholder="$t('Note')"
						v-model="comment"
					></textarea>
				</div>

				<div class="drawer-action-bar justify-end">
					<div class="flex justify-end gap-1">
						<button type="button" class="btn btn-ghost" :class="[saving ? 'hidden' : '']" @click="hide">
							{{ $t('Cancel') }}
						</button>
						<button type="submit" data-test="save" class="btn btn-primary" :disabled="!canSave">
							<span v-show="saving" class="loading loading-spinner"></span>
							{{ $t('Save') }}
						</button>
					</div>
				</div>

			</form>
		</template>
	</Drawer>
</template>

<script lang="ts">
import { PropType } from "vue";
import { mapStores } from "pinia";
import Drawer from "@/components/ui/Drawer.v2.vue";
import EvaluationLevelPicker from "@/components/EvaluationLevelPicker.vue";
import PupilSelectionSummary from "@/components/PupilSelectionSummary.vue";
import { Evaluation } from "@/models/Evaluation.model";
import { EvaluationLevel } from "@/models/EvaluationLevel.model";
import { Goal } from "@/models/Goal.model";
import { Pupil } from "@/models/Pupil.model";
import { useEvaluationLevelsStore } from "@/stores/EvaluationLevels.store";
import { useEvaluationsStore } from "@/stores/Evaluations.store";
import { ApiErrorMessageBag } from "@/stores/errors/ApiErrorMessageBag";
import GoalTitle from "@/components/GoalTitle.vue";
import { MapPinIcon } from "@heroicons/vue/24/outline";

export interface BulkEvaluationPair {
	pupil: Pupil;
	goal: Goal;
}

/**
 * Applies one evaluation level and one shared note to every selected pupil/goal pair.
 * The selection itself is owned by the matrix and can still change while the drawer is
 * open (a pupil or goal leaving the view); the drawer re-renders from the `pairs` prop.
 */
export default {
	components: {
		GoalTitle,
		Drawer,
		EvaluationLevelPicker,
		PupilSelectionSummary,
		MapPinIcon,
	},

	props: {
		pairs: {
			type: Array as PropType<BulkEvaluationPair[]>,
			required: true,
		},
		/** Every pupil the selection could span; "Everyone" in the header means all of these. */
		allPupils: {
			type: Array as PropType<Pupil[]>,
			required: true,
		},
	},

	emits: ['saved', 'requestClose'],

	data() {
		return {
			level: null as EvaluationLevel | null,
			comment: '',
			saving: false,
		};
	},

	computed: {
		...mapStores(useEvaluationLevelsStore, useEvaluationsStore),

		/**
		 * The same pupil/goal pair list is confusing summarised as one flat "distinct pupils"
		 * count: with several goals selected, it can look like every pupil is evaluated on
		 * every goal, when a pupil may only be selected for one of them. So each goal in the
		 * list shows its own selection instead, keyed off this per-goal grouping.
		 */
		pupilsByGoal(): Map<string, Pupil[]> {
			const map = new Map<string, Pupil[]>();
			this.pairs.forEach((pair: BulkEvaluationPair) => {
				const pupils = map.get(pair.goal.id);
				if (pupils) {
					pupils.push(pair.pupil);
				} else {
					map.set(pair.goal.id, [pair.pupil]);
				}
			});
			return map;
		},

		distinctGoals(): Goal[] {
			const seen = new Map<string, Goal>();
			this.pairs.forEach((pair: BulkEvaluationPair) => {
				if (!seen.has(pair.goal.id)) {
					seen.set(pair.goal.id, pair.goal);
				}
			});
			return Array.from(seen.values());
		},

		canSave(): boolean {
			return this.level !== null && !this.saving && this.pairs.length > 0;
		},
	},

	mounted() {
		this.evaluationLevelsStore.loadOnce();
	},

	watch: {
		pairs(pairs: BulkEvaluationPair[]) {
			// A save in flight already snapshotted its own pairs; closing here would
			// unmount the drawer mid-save and strand the pending 'saved' emit.
			if (pairs.length === 0 && !this.saving) {
				this.$emit('requestClose');
			}
		},
		level(level: EvaluationLevel | null) {
			if (level) {
				setTimeout(() => (this.$refs['note'] as HTMLTextAreaElement | undefined)?.focus(), 100);
			}
		},
	},

	methods: {
		pupilsForGoal(goal: Goal): Pupil[] {
			return this.pupilsByGoal.get(goal.id) ?? [];
		},

		async save() {
			if (!this.canSave || !this.level) {
				return;
			}

			this.saving = true;

			const pairs: BulkEvaluationPair[] = [...this.pairs];
			const evaluations = pairs.map((pair: BulkEvaluationPair) => {
				const evaluation: Evaluation = this.evaluationsStore.new();
				evaluation.evaluationLevel = this.level!;
				evaluation.pupilId = pair.pupil.id;
				evaluation.goalId = pair.goal.id;
				evaluation.comment = this.comment;
				evaluation.goalCollectionId = pair.goal.goalCollectionId ?? undefined;
				return evaluation;
			});

			try {
				await this.evaluationsStore.bulkCreate(evaluations);
			} catch (e) {
				this.saving = false;
				// Bulk field errors are keyed `{index}.pupil.id` and mean nothing to a teacher;
				// only the generic error (network, 403, 500) is shown.
				this.$notify({
					text: e instanceof ApiErrorMessageBag && e.generic
						? e.generic.toLocaleString(this.$i18n)
						: this.$t('An error occurred'),
					type: 'error',
				});
				return;
			}

			// Goals that were not loaded through a store are not reached by afterSave;
			// mirror the single editor and attach the evaluation to the given instance.
			pairs.forEach((pair: BulkEvaluationPair, index: number) => {
				const evaluation = evaluations[index];
				if (!pair.goal.getEvaluations(pair.pupil).find((existing: Evaluation) => existing.id === evaluation.id)) {
					pair.goal.addEvaluation(pair.pupil.id, evaluation);
				}
			});

			this.saving = false;
			this.$notify({
				text: this.$tc('Evaluation saved', evaluations.length),
				type: 'success',
			});
			this.$emit('saved', evaluations);
		},
	},
}
</script>
